import { cn } from "@/lib/utils";
import type { Action, ActionType, ActionResult, RerollPreference, Player, RerollType } from "../types";
import { ACTION_CATEGORIES, getResultsForAction } from "../constants";
import { getBaseProbability, canUseSkillReroll } from "../lib/probability";
import { useLanguage } from "../i18n";

export interface ActionItemProps {
  action: Action;
  player: Player | null;
  onUpdateType: (type: ActionType) => void;
  onUpdateResult: (result: ActionResult) => void;
  onUpdateReroll: (reroll: RerollPreference) => void;
  onRemove: () => void;
  className?: string;
}

function getRerollStatus(
  action: Action,
  player: Player | null,
  rerollLabels: Record<string, string>
): { type: RerollType; label: string } {
  if (action.rerollPreference === "none") {
    return { type: "none", label: rerollLabels.none ?? "None" };
  }

  const hasSkill = canUseSkillReroll(action.type, player);
  if (hasSkill && action.rerollPreference !== "team") {
    return { type: "skill", label: rerollLabels.skill ?? "Skill" };
  }

  if (action.rerollPreference === "team" || action.rerollPreference === "auto") {
    return { type: "team", label: rerollLabels.team ?? "Team" };
  }

  return { type: "none", label: rerollLabels.none ?? "None" };
}

export function ActionItem({
  action,
  player,
  onUpdateType,
  onUpdateResult,
  onUpdateReroll,
  onRemove,
  className,
}: ActionItemProps) {
  const { t } = useLanguage();
  const actionTypes = Object.keys(ACTION_CATEGORIES) as ActionType[];
  const results = getResultsForAction(action.type);
  const baseProb = getBaseProbability(action.type, action.result);
  const rerollStatus = getRerollStatus(action, player, t.rerollTypes);

  // Get localized result label
  const getLocalizedResultLabel = (result: ActionResult): string => {
    if (result in t.blockResults) {
      return t.blockResults[result as keyof typeof t.blockResults];
    }
    if (result in t.injuryResults) {
      return t.injuryResults[result as keyof typeof t.injuryResults];
    }
    return result; // Dice results like "2+", "3+", etc.
  };

  return (
    <div
      className={cn(
        "bg-bb-black border border-bb-gray-mid p-3 mb-2",
        className
      )}
    >
      <div className="flex flex-wrap gap-3 items-center">
        {/* Action Type */}
        <div className="flex flex-col gap-1">
          <label className="text-[0.7rem] uppercase text-bb-bone-white/50">{t.action}</label>
          <select
            value={action.type}
            onChange={(e) => onUpdateType(e.target.value as ActionType)}
            className={cn(
              "px-2 py-1.5 text-sm",
              "bg-bb-gray-mid border-2 border-bb-gray-mid rounded",
              "text-bb-bone-white cursor-pointer",
              "hover:border-bb-blood-red",
              "focus:border-bb-gold focus:outline-none"
            )}
          >
            {actionTypes.map((type) => (
              <option key={type} value={type}>
                {t.actionTypes[type]}
              </option>
            ))}
          </select>
        </div>

        {/* Result */}
        <div className="flex flex-col gap-1">
          <label className="text-[0.7rem] uppercase text-bb-bone-white/50">{t.result}</label>
          <select
            value={action.result}
            onChange={(e) => onUpdateResult(e.target.value as ActionResult)}
            className={cn(
              "px-2 py-1.5 text-sm",
              "bg-bb-gray-mid border-2 border-bb-gray-mid rounded",
              "text-bb-bone-white cursor-pointer",
              "hover:border-bb-blood-red",
              "focus:border-bb-gold focus:outline-none"
            )}
          >
            {results.map((result) => (
              <option key={result} value={result}>
                {getLocalizedResultLabel(result)}
              </option>
            ))}
          </select>
        </div>

        {/* Reroll Preference */}
        <div className="flex flex-col gap-1">
          <label className="text-[0.7rem] uppercase text-bb-bone-white/50">{t.reroll}</label>
          <select
            value={action.rerollPreference}
            onChange={(e) => onUpdateReroll(e.target.value as RerollPreference)}
            className={cn(
              "px-2 py-1.5 text-sm",
              "bg-bb-gray-mid border-2 border-bb-gray-mid rounded",
              "text-bb-bone-white cursor-pointer",
              "hover:border-bb-blood-red",
              "focus:border-bb-gold focus:outline-none"
            )}
          >
            <option value="auto">{t.rerollOptions.auto}</option>
            <option value="none">{t.rerollOptions.none}</option>
            <option value="team">{t.rerollOptions.team}</option>
          </select>
        </div>

        {/* Reroll Status Badge */}
        <span
          className={cn(
            "text-xs px-2 py-1 rounded-sm whitespace-nowrap",
            rerollStatus.type === "skill" && "bg-bb-gold/20 text-bb-gold border border-bb-gold",
            rerollStatus.type === "skill-shared" && "bg-bb-gold/20 text-bb-gold border border-bb-gold",
            rerollStatus.type === "team" && "bg-bb-pitch-green/30 text-green-400 border border-bb-pitch-green",
            rerollStatus.type === "none" && "bg-bb-blood-red/20 text-red-400 border border-bb-blood-red"
          )}
        >
          {rerollStatus.label}
        </span>

        {/* Base Probability */}
        <span className="font-display text-xl text-bb-gold ml-auto">
          {(baseProb * 100).toFixed(1)}%
        </span>

        {/* Remove Button */}
        <button
          type="button"
          onClick={onRemove}
          className={cn(
            "font-condensed text-sm font-semibold uppercase tracking-wide",
            "px-2 py-1 transition-all",
            "bg-transparent text-bb-blood-bright border border-bb-blood-red",
            "hover:bg-bb-blood-red hover:text-bb-bone-white"
          )}
          aria-label={t.removeAction}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
